import React, { useState } from 'react';
import cx from 'classnames';

import FormattedMessage from 'components/formatted-message';
import { PlayerProfileFullDto, TeamDto } from 'utils/api/dto';
import { FAV_AND_RECENT_TYPE_SCOUT_PLAYER, FAV_AND_RECENT_TYPE_TEAM } from 'widgets/favorites/constant';

import { PlayerRow, TeamRow } from '../dropdown-row';
import FilterPills, { SearchFilter } from '../filter-pills';
import styles from '../search-dropdown.module.css';
import { SelectItemHandler } from '../types';

export type FavoritePlayer = PlayerProfileFullDto & { playerPosition?: string | null };

/** A favorite team: TeamDto found by the id of the favorite item, so the id is known */
export type FavoriteTeam = TeamDto & { id: number };

type FavoritesSectionProps = {
    players: FavoritePlayer[];
    teams: FavoriteTeam[];
    getPhotoUrl: (player: FavoritePlayer) => string | null;
    onSelect: SelectItemHandler;
    onViewAll: () => void;
};

const FavoritesStarIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
    </svg>
);

const FavoritesSection = ({ players, teams, getPhotoUrl, onSelect, onViewAll }: FavoritesSectionProps) => {
    const [filter, setFilter] = useState<SearchFilter>('all');
    const visibleTeams = filter === 'players' ? [] : teams;
    const visiblePlayers = filter === 'teams' ? [] : players;

    return (
        <div className={cx(styles.section, styles.favoritesSection)}>
            <div className={styles.favoritesHeader}>
                <div className={styles.favoritesTitle}>
                    <FavoritesStarIcon />
                    <FormattedMessage id="home.favorites" />
                </div>
                <button type="button" className={styles.viewAllButton} onClick={onViewAll}>
                    <FormattedMessage id="searchbar.viewAll" />
                </button>
            </div>
            <FilterPills value={filter} onChange={setFilter} />
            <div className={styles.list}>
                {visibleTeams.map(team => (
                    <TeamRow
                        key={`team-${team.id}`}
                        name={team.nameEn}
                        logo={team.logo}
                        meta={team.acronymEn}
                        onClick={() => onSelect(team.id, FAV_AND_RECENT_TYPE_TEAM)}
                    />
                ))}
                {visiblePlayers.map(player => (
                    <PlayerRow
                        key={`player-${player.id}`}
                        player={player}
                        photoUrl={getPhotoUrl(player)}
                        meta={typeof player.params?.teamName === 'string' ? player.params.teamName : null}
                        onClick={() => onSelect(player.id, FAV_AND_RECENT_TYPE_SCOUT_PLAYER)}
                    />
                ))}
                {!visibleTeams.length && !visiblePlayers.length ? (
                    <div className={styles.empty}>
                        <FormattedMessage id="home.favorites.empty" />
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default FavoritesSection;
